/* eslint-disable multiline-ternary */
import React from 'react'
import {
  Box,
  Button,
  Heading,
  type ModalProps,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useMediaQuery,
  Text
} from '@chakra-ui/react'
import ReactPlayer from 'react-player'
import { RoomSettingsContext } from '../pages/+RoomSettingsContext'
import { type Episode } from '../pages/playalong/types'

const ScreenSizePlayalongErrorModal: React.FC<{
  currentEpisode: Episode | null
}> = ({ currentEpisode }) => {
  const [wideEnough, tallEnough] = useMediaQuery(
    ['(min-width: 1024px)', '(min-height: 768px)'],
    {
      ssr: true,
      fallback: true // return false on the server, and re-evaluate on the client side
    }
  )
  const isOpen = !(wideEnough && tallEnough)
  const { showIntroModal } = React.useContext(RoomSettingsContext)
  if (typeof window === 'undefined') {
    return null
  }
  return (
    <Modal
      size="6xl"
      isOpen={isOpen && !showIntroModal}
      onClose={() => {
        return false
      }}
    >
      <ModalOverlay />
      <ModalContent w="calc(100% - 10px)" minH="calc(90% - 10px)">
        <ModalHeader p="10px">
          <Heading textAlign="center" size="xl" mb="10px">
            Playalong <sub>beta</sub>
          </Heading>
          {currentEpisode?.videoPreview != null && (
            <Box
              as="img"
              w="100% !important"
              maxW="100% !important"
              aspectRatio="16/9"
              height="auto !important"
              objectFit="cover"
              src={currentEpisode?.videoPreview ?? ''}
            />
          )}
          <Box
            w="100% !important"
            maxW="100% !important"
            aspectRatio="16/9"
            height="auto !important"
            as={ReactPlayer}
            url="https://www.youtube.com/watch?v=Fdwj3-hfNVY&ab_channel=Reactvts"
          />
        </ModalHeader>
        <ModalBody>
          <Text lineHeight="1.5em">
            <strong>Error:</strong> Hosting a Playalong game requires a minimum
            screen size of 1024px by 768px at 100% Zoom.
          </Text>
          {Math.ceil(((window.outerWidth - 10) / window.innerWidth) * 100) >
          95 ? (
            <Heading textAlign="center" size="md" mb="1em">
              Your Browser Zoom May Be Too High
            </Heading>
          ) : (
            <Heading textAlign="center" size="md" mb="1em">
              This Window Is Too
              <br />
              Small To Host A Game
            </Heading>
          )}
          {Math.ceil(((window.outerWidth - 10) / window.innerWidth) * 100) >
          100 ? (
            <Text>
              Your Browser zoom may be set to{' '}
              {Math.ceil(((window.outerWidth - 10) / window.innerWidth) * 100)}%
              So we see it as {window.innerWidth}px by {window.innerHeight}px,
              try settings the zoom to 100%
            </Text>
          ) : (
            <Text>
              Currently this window is about {window.innerWidth}px by{' '}
              {window.innerHeight}px{' '}
            </Text>
          )}
          <Text lineHeight="1.5em">
            If you&apos;re on a mobile device and are trying to join a game,{' '}
            <Box as="a" href="/join">
              Click Here
            </Box>{' '}
          </Text>
          <Text lineHeight="1.5em">
            <Box as="a" href="/#home" textDecoration="none">
              Learn more about Reactvts on our homepage
            </Box>
          </Text>
          <Text textAlign="center"> </Text>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const ScreenSizeErrorModal: React.FC = () => {
  const [wideEnough, tallEnough] = useMediaQuery(
    ['(min-width: 1024px)', '(min-height: 768px)'],
    {
      ssr: true,
      fallback: true // return false on the server, and re-evaluate on the client side
    }
  )
  const isOpen = !(wideEnough && tallEnough)
  const { showIntroModal } = React.useContext(RoomSettingsContext)
  if (typeof window === 'undefined') {
    return null
  }
  return (
    <Modal
      isOpen={isOpen && !showIntroModal}
      onClose={() => {
        return false
      }}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {Math.ceil(((window.outerWidth - 10) / window.innerWidth) * 100) >
          95 ? (
            <Heading textAlign="center" size="lg" mb="0">
              Your Browser Zoom May Be Too High
            </Heading>
          ) : (
            <Heading textAlign="center" size="lg" mb="0">
              This Window Is Too
              <br />
              Small To Host A Game
            </Heading>
          )}
        </ModalHeader>
        <ModalBody>
          <Text mb="20px">
            Hosting a Reactvts game requires a minimum screen size of 1024px by
            768px at 100% Zoom.
          </Text>
          {Math.ceil(((window.outerWidth - 10) / window.innerWidth) * 100) >
          100 ? (
            <Text>
              Your Browser zoom may be set to{' '}
              {Math.ceil(((window.outerWidth - 10) / window.innerWidth) * 100)}%
              So we see it as {window.innerWidth}px by {window.innerHeight}px,
              try settings the zoom to 100%
            </Text>
          ) : (
            <Text>
              Currently this window is about {window.innerWidth}px by{' '}
              {window.innerHeight}px{' '}
            </Text>
          )}
          <Text>
            If you&apos;re on a mobile device and are trying to join a game,{' '}
            <Box as="a" href="/join">
              Click Here
            </Box>
          </Text>
          <Text textAlign="center">
            <Box as="a" href="/#home" textDecoration="none">
              Back to Homepage
            </Box>
          </Text>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const ErrorModal: React.FC<ModalProps> = (props) => {
  const { isOpen, onClose, children, ...rest } = props
  return (
    <Modal isOpen={isOpen} onClose={onClose} {...rest}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Error</ModalHeader>
        <ModalCloseButton />
        <ModalBody>{children}</ModalBody>

        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

interface ConfigModalProps extends ModalProps {
  title: string
}

const ConfigModal: React.FC<ConfigModalProps> = (props) => {
  const { title, isOpen, onClose, children, ...rest } = props
  return (
    <Modal isOpen={isOpen} onClose={onClose} {...rest}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>{children}</ModalBody>

        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
export {
  ErrorModal,
  ScreenSizeErrorModal,
  ConfigModal,
  ScreenSizePlayalongErrorModal
}
